import React, { useState } from "react";
import { Box, Flex, HStack, Text, Textarea, IconButton, Icon, Divider } from "@chakra-ui/react";
import { HiArrowUp } from "react-icons/hi2";
import { c } from "../../classroomColors";
import { brand, onTint } from "../../standeTokens";
import { waitedMinutes } from "./clusterQuestions";

/**
 * What a group of students actually asked, and one reply that answers all of
 * them (wireframe 130:54, the card revealed on hover).
 *
 * The card shows the individual questions rather than only a summary: the
 * teacher is about to answer on behalf of four people, and should see their
 * words before doing so.
 */
const ClusterCard = ({ cluster, now, isDark, onRespond, onDismiss }) => {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const b = brand(isDark);
  const tint = onTint(isDark);
  const minutes = waitedMinutes(cluster, now);

  const submit = async () => {
    const text = reply.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await onRespond(cluster, text);
      setReply("");
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      width="330px"
      bg={c.page}
      border="1px solid"
      borderColor={c.border}
      borderRadius="14px"
      boxShadow="0 16px 40px -16px rgba(0,0,0,0.35)"
      overflow="hidden"
    >
      <Box p="14px">
        <Flex justify="space-between" align="center" gap="8px" mb="9px">
          <HStack spacing="7px">
            <Box boxSize="7px" borderRadius="full" bg={b.secondaryOrange} />
            <Text fontSize="12px" fontWeight="500" color={c.text}>
              {cluster.count} {cluster.count === 1 ? "student" : "students"} asked
            </Text>
          </HStack>
          <Text
            fontSize="11px"
            fontWeight="500"
            px="7px"
            py="1px"
            borderRadius="full"
            bg={minutes >= 3 ? b.lightRed : b.lightYellow}
            color={minutes >= 3 ? tint.lightRed : tint.lightYellow}
          >
            {minutes < 1 ? "just now" : `${minutes} min`}
          </Text>
        </Flex>

        <Text fontSize="14px" fontWeight="500" color={c.text} lineHeight="1.4" mb="10px">
          {cluster.headline}
        </Text>

        <Divider borderColor={c.borderSoft} mb="9px" />

        <Box maxH="132px" overflowY="auto">
          {cluster.questions.map((q) => (
            <Flex key={q.id} gap="8px" mb="7px" align="flex-start">
              <Flex
                flexShrink={0}
                boxSize="20px"
                mt="1px"
                borderRadius="full"
                bg={c.surfaceSubtle}
                color={c.textSub}
                align="center"
                justify="center"
                fontSize="9.5px"
                fontWeight="600"
              >
                {(q.studentName || "?")[0].toUpperCase()}
              </Flex>
              <Box minW={0}>
                <Text fontSize="11px" color={c.textMuted} lineHeight="1.2">
                  {q.studentName}
                </Text>
                <Text fontSize="12.5px" color={c.textSub} lineHeight="1.4">
                  {q.text}
                </Text>
              </Box>
            </Flex>
          ))}
        </Box>
      </Box>

      <Flex
        align="flex-end"
        gap="8px"
        px="12px"
        py="10px"
        bg={c.surfaceSubtle}
        borderTop="1px solid"
        borderColor={c.borderSoft}
      >
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={`Respond to all ${cluster.count}`}
          variant="unstyled"
          resize="none"
          rows={1}
          minH="24px"
          maxH="80px"
          fontSize="13px"
          color={c.text}
          _placeholder={{ color: c.textMuted }}
        />
        <IconButton
          aria-label={`Respond to all ${cluster.count}`}
          icon={<Icon as={HiArrowUp} boxSize="14px" />}
          onClick={submit}
          isLoading={sending}
          size="xs"
          minW="26px"
          h="26px"
          borderRadius="full"
          bg={reply.trim() ? c.solidBg : c.hover}
          color={reply.trim() ? c.solidText : c.textMuted}
          _hover={{ opacity: 0.88 }}
          flexShrink={0}
        />
      </Flex>

      {onDismiss && (
        <Box
          as="button"
          type="button"
          width="100%"
          py="7px"
          fontSize="11.5px"
          color={c.textMuted}
          borderTop="1px solid"
          borderColor={c.borderSoft}
          _hover={{ bg: c.hover, color: c.text }}
          onClick={() => onDismiss(cluster)}
        >
          Mark as covered
        </Box>
      )}
    </Box>
  );
};

export default ClusterCard;
